import type { ArticleItem } from '@components/pages/ResourceSections';

/**
 * Home page copy, in one mutable object.
 *
 * The landing sections held their copy as module constants, which are evaluated
 * on import — before the CMS has answered. Holding it here instead lets the copy
 * be read at render time, so `hydrateSiteContent` can fill it in first and the
 * sections need no loading state of their own.
 *
 * The values below are what the page shipped with. They stand until the CMS
 * says otherwise, which is what keeps the site readable with the API down.
 */

export interface SolutionDetail {
    title: string;
    body: string;
}

/** One slide of the "Our Solutions" band. */
export interface Solution {
    num: string;
    /** Olive-tinted lead-in of the heading. */
    headTop: string;
    /** Dark remainder. */
    headBottom: string;
    paragraph: string;
    details: SolutionDetail[];
    /** The slide's photograph; the bundled statue stands in when empty. */
    image: string;
    imageAlt: string;
    /** Stories the CMS attached, resolved against the stories pool once it has answered. */
    stories?: ArticleItem[];
}

export interface PromoCard {
    /** Empty means the bundled photograph. */
    image: string;
    imageAlt: string;
    title: string;
    body: string;
}

export interface Figure {
    /** As written: "10k", "09k", "20M", "6,500+". The band animates the number in it. */
    value: string;
    label: string;
}

export interface HomeContent {
    hero: {
        /** The three animated words, in the order they land. */
        words: string[];
        paragraph: string;
        statValue: string;
        statLabel: string;
        ctaLabel: string;
        /** Where the call to action goes. */
        ctaHref: string;
        railHeading: string;
        /** Background photograph; the bundled one stands in when empty. */
        background: string;
        backgroundAlt: string;
    };
    tackled: { heading: string; solutions: Solution[] };
    metrics: { heading: string; figures: Figure[] };
    innovations: { intro: string };
    video: {
        /** Light lead-in of the band title. */
        heading: string;
        /** Dark remainder, which takes the orange full stop. */
        headingBold: string;
        paragraph: string;
        galleryCaption: string;
    };
    legalAid: {
        /** First word on its own line, the rest bold; a trailing "?" takes the accent. */
        heading: string;
        paragraph: string;
        ctaLabel: string;
        /** `#legal-aid-form` opens the request form on the page; a path or URL sends visitors there. */
        ctaHref: string;
        stat: Figure;
        wideCard: PromoCard;
        tallCard: PromoCard;
    };
    blog: { titleLead: string; titleBold: string; blurb: string };
    justicePerformance: { titleLead: string; titleBold: string; blurb: string; ctaLabel: string; ctaHref: string };
    social: { titleLead: string; titleBold: string; blurb: string };
}

/** The request form the legal-aid button opens when its link is this. */
export const LEGAL_AID_FORM = '#legal-aid-form';

const SOLUTIONS: Solution[] = [
    {
        num: '01',
        image: '',
        imageAlt: '',
        headTop: 'Here is how ',
        headBottom: 'we’ve tackled it.',
        paragraph:
            'Most people held in pre-trial detention are not there because they were convicted. They are there because nobody was available to file a bail application, chase a case file or turn up on the day. So we put lawyers where the delay actually happens — in police stations and magistrate courts, on the days cases are listed.',
        details: [
            {
                title: 'The Problem',
                body: 'A case can sit untouched for months, not for want of a defence but for want of anyone to move it. Files go missing, hearings are adjourned without notice, and the person in the cell is the last to hear about any of it.',
            },
            {
                title: 'The Target Audience',
                body: 'People in pre-trial detention who cannot afford a lawyer, and the families trying to trace where their relative is being held and what they have been charged with.',
            },
            {
                title: 'The Goal',
                body: 'Get a qualified lawyer onto every eligible file within days rather than months, and keep the case moving until it is heard, withdrawn or the person is released.',
            },
        ],
    },
    {
        num: '02',
        image: '',
        imageAlt: '',
        headTop: 'Justice built ',
        headBottom: 'for everyone.',
        paragraph:
            'Knowing your rights is worth little if the next step costs more than you earn in a month. Podus puts guidance, document drafting, verification and a route to a real lawyer in one place — in plain language, on the phone people already own.',
        details: [
            {
                title: 'The Problem',
                body: 'Legal help is priced and worded for people who already have it. For everyone else the first consultation is a barrier, and the paperwork is written to be understood by lawyers rather than by the person signing it.',
            },
            {
                title: 'The Target Audience',
                body: 'Individuals, small traders and growing businesses who need a contract, a check or an answer, and cannot justify a retainer to get one.',
            },
            {
                title: 'The Goal',
                body: 'Make the first step free and the next step affordable, so that getting legal help is an ordinary decision rather than a last resort.',
            },
        ],
    },
    {
        num: '03',
        image: '',
        imageAlt: '',
        headTop: 'Reaching those ',
        headBottom: 'who need it most.',
        paragraph:
            'The people with the least access are rarely the ones filling in forms online. We work through community paralegals, partner organisations, radio and local languages so that help reaches households that would never have found it otherwise.',
        details: [
            {
                title: 'The Problem',
                body: 'Digital services quietly select for people who already have data, literacy and confidence in institutions. The households in the greatest difficulty are the least likely to be reached by them.',
            },
            {
                title: 'The Target Audience',
                body: 'Rural and low-income communities, survivors of gender-based violence, and anyone whose first contact with the justice system happens through someone they already trust.',
            },
            {
                title: 'The Goal',
                body: 'Meet people where they already are — through community networks and local languages — and make sure a referral into formal legal help actually completes.',
            },
        ],
    },
];

export const HOME: HomeContent = {
    hero: {
        words: ['Justice', 'Technology', 'Accountability'],
        paragraph:
            'Citizens’ Gavel is a civic-technology organisation making justice faster, fairer and easier to reach — pairing legal expertise with open data to move stalled cases and put real help within reach.',
        statValue: '12M+',
        statLabel: 'Lives Reached.',
        ctaLabel: 'Discover',
        ctaHref: '/projects',
        railHeading: 'Our Top Projects',
        background: '',
        backgroundAlt: '',
    },
    tackled: { heading: 'Here is how we’ve tackled it.', solutions: SOLUTIONS },
    metrics: {
        heading: 'What happens when access, accountability, and technology come together.',
        figures: [
            { value: '6.5k', label: 'Cases Received' },
            { value: '5.6k', label: 'Cases Resolved' },
            { value: '12M', label: 'People Reached' },
        ],
    },
    innovations: {
        intro: 'Our programs are designed to confront injustice directly — supporting individuals, challenging systemic abuse, and advancing reforms that build a fairer and more accountable justice system.',
    },
    video: {
        heading: 'Moments that capture our',
        headingBold: 'work in action',
        paragraph:
            'Inside the courtrooms, cells and community halls where our work actually happens — told by the people who lived it.',
        galleryCaption:
            'Behind every case resolved, every right protected, and every voice heard is a person who finally found a way forward. These stories capture what happens when justice becomes accessible: fear gives way to clarity,',
    },
    legalAid: {
        heading: 'Need Legal Aid?',
        paragraph:
            'Podus Contract is a smart legal tool designed to help individuals and businesses create, review, and manage contracts with ease. By combining AI-powered analysis with expert-backed legal standards along side human',
        ctaLabel: 'Get Started Here',
        ctaHref: LEGAL_AID_FORM,
        stat: { value: '33k+', label: 'Cases resolved' },
        wideCard: {
            image: '',
            imageAlt: '',
            title: 'No fees. No queues.',
            body: 'Legal aid through Podus is free for those who qualify, and the request takes minutes.',
        },
        tallCard: {
            image: '',
            imageAlt: '',
            title: 'Help that arrives before it is too late.',
            body: 'Tell us what happened and we will match you with a lawyer who can take it on.',
        },
    },
    blog: {
        titleLead: 'Our ',
        titleBold: 'Blog',
        blurb: 'Behind every case resolved, every right protected, and every voice heard is a person who finally found a way forward. These stories capture what happens when justice becomes accessible.',
    },
    justicePerformance: {
        titleLead: 'The Court ',
        titleBold: 'Rankings',
        blurb: 'Behind every case resolved, every right protected, and every voice heard is a person who finally found a way forward. These stories capture what happens when justice becomes accessible: fear gives way to clarity,',
        ctaLabel: '',
        ctaHref: '/resources/court-ranking',
    },
    social: {
        titleLead: 'Our ',
        titleBold: 'Social media',
        blurb: 'Behind every case resolved, every right protected, and every voice heard is a person who finally found a way forward. These stories capture what happens when justice becomes accessible.',
    },
};

/** "Need Legal Aid?" → "Need" light on its own line, "Legal Aid" bold, "?" in the accent colour. */
export function legalAidHeading(heading: string): { lead: string; bold: string; mark: string } {
    const trimmed = heading.trim();
    const mark = /[?!]$/.test(trimmed) ? trimmed.slice(-1) : '';
    const words = (mark ? trimmed.slice(0, -1) : trimmed).trim().split(/\s+/).filter(Boolean);
    if (words.length <= 1) return { lead: '', bold: words[0] ?? '', mark };
    return { lead: words[0], bold: words.slice(1).join(' '), mark };
}

/** "33k+" → "33k" and "+", so the plus can take the accent colour. */
export function statParts(value: string): { figure: string; plus: string } {
    const m = /^(.*?)(\+?)$/.exec(value.trim());
    return { figure: m?.[1] ?? value, plus: m?.[2] ?? '' };
}
